/**
 * Test data: variables referenced in test steps as {{NAME}}.
 *  - Organization variables (shared by all projects; managed by Project Admins)
 *  - Project variables (managed by members; override an org variable with the same key)
 * Only the creator (or a Project Admin) changes or deletes a variable; others can copy it.
 *  - Built-ins ({{$timestamp}}, {{$randomEmail}}, ...) generated fresh on every run
 * Secret variables are stored encrypted, never returned by the API, and reach the browser only
 * through e2e's secrets mechanism (masked from the model, logs, traces and reports), so they are
 * allowed in "act" steps only.
 *
 * A placeholder can keep the text it replaced as a written value: {{EMAIL|qa@example.com}}. A run
 * uses it when neither the run's test data collection nor the shared values have EMAIL, and the
 * report says so (StepDataNote). Secrets never have a written value (it would sit in the test as
 * plain text).
 */
import { z } from 'zod';
import { appUrlProblem } from './collections.js';
import type { StepKind, TestStep } from './testing.js';

export const VARIABLE_KEY_RE = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
/**
 * {{NAME}} or {{$builtin}} with optional inner spaces, optionally with a written value used when the
 * test data has no NAME: {{NAME|written value}} (group 2; no braces inside).
 */
export const PLACEHOLDER_RE = /\{\{\s*(\$?[A-Za-z][A-Za-z0-9_]*)\s*(?:\|([^{}]*))?\}\}/g;

/** Text that can be kept as a written value (no braces: they would end the placeholder). */
export const canBeWrittenValue = (text: string) => text.trim().length > 0 && !/[{}]/.test(text);

/** `{{NAME}}`, or `{{NAME|written value}}` when a written value is given. */
export function placeholderText(name: string, written?: string): string {
  return written !== undefined && canBeWrittenValue(written) ? `{{${name}|${written.trim()}}}` : `{{${name}}}`;
}

export const BUILTIN_VARIABLES = {
  $timestamp: 'Current time in milliseconds, e.g. 1792606734000',
  $date: "Today's date, e.g. 2026-10-22",
  $uuid: 'A random UUID',
  $randomNumber: 'A random 6-digit number',
  $randomEmail: 'A unique email address, e.g. qa+1792606734000@example.test',
} as const;
export type BuiltinVariable = keyof typeof BUILTIN_VARIABLES;
export const isBuiltin = (name: string): name is BuiltinVariable => Object.hasOwn(BUILTIN_VARIABLES, name);

/** e2e redacts secrets by value and rejects values shorter than 6 characters. */
export const SECRET_MIN_LENGTH = 6;

export const variableInputSchema = z
  .object({
    key: z.string().regex(VARIABLE_KEY_RE, 'Use letters, digits and _ (start with a letter), max 64 characters'),
    value: z.string().max(10_000),
    isSecret: z.boolean().default(false),
    description: z.string().max(500).nullish(),
    /** Test data collection the value belongs to (null/omitted: the default values). */
    collectionId: z.string().min(1).max(64).nullish(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.isSecret && v.value.length < SECRET_MIN_LENGTH) {
      ctx.addIssue({ code: 'custom', path: ['value'], message: `Secret values need at least ${SECRET_MIN_LENGTH} characters` });
    }
    const appUrl = appUrlProblem(v.key, v.value, v.isSecret);
    if (appUrl) ctx.addIssue({ code: 'custom', path: ['value'], message: appUrl });
  });
export type VariableInput = z.infer<typeof variableInputSchema>;

/** Update: value omitted = keep (needed for secrets, whose value the client never sees). */
export const variableUpdateSchema = z
  .object({
    key: z.string().regex(VARIABLE_KEY_RE).optional(),
    value: z.string().max(10_000).optional(),
    isSecret: z.boolean().optional(),
    description: z.string().max(500).nullish(),
  })
  .strict();
export type VariableUpdate = z.infer<typeof variableUpdateSchema>;

/**
 * Copy: a new key (keys are unique per scope and collection), optionally a project to copy into and
 * a collection (omitted: the source's collection when it is available there, else the default values).
 */
export const variableCopySchema = z
  .object({
    key: z.string().regex(VARIABLE_KEY_RE, 'Use letters, digits and _ (start with a letter), max 64 characters'),
    projectId: z.string().min(1).nullish(),
    collectionId: z.string().min(1).max(64).nullish(),
  })
  .strict();
export type VariableCopy = z.infer<typeof variableCopySchema>;

export type VariableScope = 'ORG' | 'PROJECT';

/** API response shape. `value` is null for secrets. */
export interface VariableView {
  id: string;
  key: string;
  value: string | null;
  isSecret: boolean;
  description: string | null;
  scope: VariableScope;
  /** Test data collection (null: the default values). */
  collectionId: string | null;
  /** Project list only: hidden by a project value with the same key in the same collection (or defaults). */
  overridden?: boolean;
  createdBy: { id: string; name: string };
  updatedAt: string;
}

/** Names referenced in a text, in order of first appearance. */
export function findPlaceholders(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(PLACEHOLDER_RE)) if (!out.includes(m[1]!)) out.push(m[1]!);
  return out;
}

export interface PlaceholderRef {
  name: string;
  /** The written value ({{NAME|written}}), trimmed; undefined when there is none. */
  written?: string;
}

/** Every placeholder occurrence in a text, in order (a name can appear twice with different written values). */
export function findPlaceholderRefs(text: string): PlaceholderRef[] {
  return [...text.matchAll(PLACEHOLDER_RE)].map((m) => ({ name: m[1]!, ...(m[2] !== undefined ? { written: m[2].trim() } : {}) }));
}

export interface VariableProblem {
  stepId: string;
  name: string;
  problem: 'unknown' | 'secret-not-allowed' | 'secret-written-value';
  message: string;
}

const KIND_LABEL: Record<StepKind, string> = { open: 'Open page', act: 'Do something', assert: 'Check something', wait: 'Wait' };

/**
 * Validates the {{...}} references of a test against the variables available to its project.
 * `available`: key -> isSecret (org + project, merged).
 */
export function checkStepVariables(steps: TestStep[], available: Map<string, boolean>): VariableProblem[] {
  const problems: VariableProblem[] = [];
  for (const step of steps) {
    const refs = findPlaceholderRefs(step.value);
    for (const name of findPlaceholders(step.value)) {
      if (isBuiltin(name)) continue;
      const written = refs.some((r) => r.name === name && r.written !== undefined);
      if (!available.has(name)) {
        // A written value covers a name no test data has (yet).
        if (!refs.every((r) => r.name !== name || r.written !== undefined)) {
          problems.push({
            stepId: step.id,
            name,
            problem: 'unknown',
            message: `Unknown test data "{{${name}}}". Add it to Test data, or write the value to use instead: {{${name}|value}}`,
          });
        }
      } else if (available.get(name) && written) {
        problems.push({
          stepId: step.id,
          name,
          problem: 'secret-written-value',
          message: `"{{${name}}}" is secret, so it cannot have a written value in the test (anyone could read it). Use {{${name}}} without "|…"`,
        });
      } else if (available.get(name) && step.kind !== 'act') {
        problems.push({
          stepId: step.id,
          name,
          problem: 'secret-not-allowed',
          message: `Secret "{{${name}}}" can only be used in "Do something" steps, not in "${KIND_LABEL[step.kind]}"`,
        });
      }
    }
  }
  return problems;
}

/** Where a step's test data came from, when it is worth telling (shown in the report under the step). */
export interface StepDataNote {
  name: string;
  /** `default`: the run's collection has no such value, the shared value was used. `written`: no test data has it, the value written in the test was used. */
  source: 'default' | 'written';
  /** The written value (already part of the test, never a secret); absent for `default`. */
  value?: string;
  /** Why, in plain language (e.g. `EMAIL is not in the "Staging" test data collection or the shared values.`). */
  reason: string;
}

/** What a run has: which keys exist (key -> isSecret) and which of them come from the run's collection. */
export interface RunDataSources {
  kinds: Map<string, boolean>;
  fromCollection: Set<string>;
}

/**
 * Applies written values for a run: `{{NAME|written}}` becomes the written text when the run's test
 * data has no NAME, and plain `{{NAME}}` when it has. Returns the steps to execute and, per step, notes
 * for the report (values the collection did not provide).
 */
export function applyWrittenValues(
  steps: TestStep[],
  data: RunDataSources,
  collectionName: string | null,
): { steps: TestStep[]; notes: StepDataNote[][] } {
  const where = collectionName ? `the "${collectionName}" test data collection or the shared values` : 'the shared values';
  const notes = steps.map(() => [] as StepDataNote[]);
  const out = steps.map((step, i) => {
    const add = (note: StepDataNote) => {
      if (!notes[i]!.some((n) => n.name === note.name && n.source === note.source && n.value === note.value)) notes[i]!.push(note);
    };
    const value = step.value.replace(PLACEHOLDER_RE, (whole, name: string, written: string | undefined) => {
      if (isBuiltin(name)) return `{{${name}}}`;
      if (data.kinds.has(name)) {
        if (collectionName && !data.fromCollection.has(name)) {
          add({ name, source: 'default', reason: `The "${collectionName}" test data collection has no ${name}.` });
        }
        return `{{${name}}}`;
      }
      if (written === undefined) return whole;
      const text = written.trim();
      add({ name, source: 'written', value: text, reason: `${name} is not in ${where}.` });
      return text;
    });
    return value === step.value ? step : { ...step, value };
  });
  return { steps: out, notes };
}
